
import styles from './CartProduct.scss';

import React, { FC, useEffect, useState } from 'react';

import NativeSelect from '@material-ui/core/NativeSelect';
import { captureException } from '@sentry/browser';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ContentType, LinkVariant, ProductModule } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { useCart } from 'src/components/Cart/UseCart';
import { MAX_QUANTITY } from 'src/components/Cart/Util';
import { ProductModel } from 'src/components/Cart/models';
import { Grid } from 'src/components/Grid/Grid';
import { Image } from 'src/components/Image/Image';
import { ProductModal } from 'src/components/ProductModal/ProductModal';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useCMSFilteredByDcsId } from 'src/hooks/UseCMSByFilters';
import naImage from 'src/images/na.png';

interface CartProductProps {
  productData?: {
    product?: ProductModel;
    removeProductById?: (productId: string) => void;
  };
}

const cartNumberList = Array.from({ length: 10 }, (v, i) => i + 1);
const MAX_QUANTITY_STRING = '11+';
const updatedCartNumberList = [...cartNumberList, MAX_QUANTITY];

const changePhoneNumberDynamically = () => {
  if (window.Invoca) {
    try {
      window.Invoca.PNAPI.run();
    } catch (err) {
      let msg = err.message;
      if (err.name === 'SecurityError') {
        msg += ' Cookies are most likely disabled on the browser.';
      }
      const e = new Error(msg);
      e.stack = err;
      e.name = 'InvocaLoadError';
      captureException(e);
      console.error('Unable to run Invoca', e);
    }
  }
};

export const CartProduct: FC<CartProductProps> = ({ productData }) => {
    if (!productData) {
      return null;
    }
    const { updateProductQuantityById, cart } = useCart();

    const { product, removeProductById } = productData || {};
    const { id, imageURL, name, quantity, totalCost } = product || {};

    const { t } = useTranslation();
    const [modalState, toggleModal] = useState(false);
    const [showQuantityDisclaimer, setQuantityDisclaimer] = useState<boolean>(false);
    const entries = useCMSFilteredByDcsId<ProductModule>(ContentType.PRODUCT_MODULE, id);
    const productDetails = entries && entries.data;
    const cartProductTotalCost = (totalCost && quantity) ? (totalCost * Number(quantity)) : '';

    const setProductQuantity = (event: React.ChangeEvent<HTMLSelectElement>, showDisclaimer: boolean = false) => {
      setQuantityDisclaimer(showDisclaimer);
      updateProductQuantityById(((id) ? id : ''), Number(event.currentTarget.value));
    };

    const getDisplayOption = (option: number) => {
      if (option === MAX_QUANTITY) {
        return MAX_QUANTITY_STRING;
      }
      return option;
    };

    useEffect(() => {
      if (!cart) return;

      changePhoneNumberDynamically();
    }, [cart.bundle?.hardware]);

    return (
      <Grid item xs={12} className={styles.cartItemCellContainer}>
        <Grid item md={12} xs={12} className={styles.cartProductCell}>
        <Image
          src={imageURL || naImage}
          className={cx(styles.cartProductCellImage, { [styles.cartProductCellImagePointer] : productDetails })}
          aria-hidden="true"
          onClick={productDetails ? () => toggleModal(true) : undefined}
        />
        <Grid className={styles.cartProductCellNumberContainer}>
          <Button
            className={styles.cartProductCellButton}
            onClick={productDetails ? () => toggleModal(true) : undefined}
          >
            {name}
          </Button>
          <NativeSelect
            className={styles.cartProductCellNumberList}
            disableUnderline
            inputProps={{ 'aria-label': `${t('cart-page.quantity')}` }}
            defaultValue={quantity}
            onChange={event => Number(event.currentTarget.value) === MAX_QUANTITY ? setProductQuantity(event, true) : setProductQuantity(event, false)}
            data-test="quantity-dropdown"
          >
          {updatedCartNumberList.map((optionVal, index) => <option value={optionVal} key={index}>{getDisplayOption(optionVal)}</option>)}
          </NativeSelect>
          {removeProductById && id && <Button variant={LinkVariant.TEXT} className={styles.cartProductCellRemove} onClick={() => removeProductById(id)}>{t('remove')}</Button>}
        </Grid>
        <div className={styles.cartProductCellPriceList}>
          <TextV2
            value={`${t('cart-page.item-cost', { price: cartProductTotalCost })}`}
            className={styles.cartProductCellCost}
            tagName="p"
            variant="body"
          />
          {(quantity && quantity > 1) && (
            <TextV2
              value={`${t('cart-page.each-item-cost', { price: totalCost })}`}
              className={styles.cartProductCellEachCost}
              tagName="p"
              variant="body-small"
            />
          )}
        </div>
      {productDetails && <ProductModal closeModal={() => toggleModal(bool => !bool)} modalOpen={modalState} productDetails={productDetails} />}
      </Grid>
      {(showQuantityDisclaimer || quantity === MAX_QUANTITY) && (
        <div className={styles.quantityDisclaimer}>
          <TextV2 value={`${t('cart-page.disclaimer')}`} variant="body" isMarkdown tagName="p" />
        </div>
      )}
    </Grid>
    );
  };

  CartProduct.displayName = 'CartProduct';
